export interface FirestoreTimestampLike {
  seconds: number;
  nanoseconds: number;
  toDate(): Date;
  toMillis(): number;
}

// ============================================================================
// SCRIPTURE TYPES
// ============================================================================

export type BibleTranslation =
  | 'NIV' | 'ESV' | 'KJV' | 'NKJV' | 'NLT'
  | 'CSB' | 'NASB20' | 'NASB95' | 'LSB'
  | 'AMP' | 'NET' | 'LXX';

export const BIBLE_TRANSLATIONS: { value: BibleTranslation; label: string }[] = [
  { value: 'NIV', label: 'NIV' },
  { value: 'ESV', label: 'ESV' },
  { value: 'KJV', label: 'KJV' },
  { value: 'NKJV', label: 'NKJV' },
  { value: 'NLT', label: 'NLT' },
  { value: 'CSB', label: 'CSB' },
  { value: 'NASB20', label: 'NASB 2020' },
  { value: 'NASB95', label: 'NASB 1995' },
  { value: 'LSB', label: 'LSB' },
  { value: 'AMP', label: 'AMP' },
  { value: 'NET', label: 'NET' },
  { value: 'LXX', label: 'LXX (Septuagint)' },
];

export interface ScriptureReference {
  reference: string;
  text: string;
  translation: BibleTranslation;
  bookAbbr: string;
  chapter: number;
  startVerse: number;
}

// ============================================================================
// MESSAGE TYPES
// ============================================================================

export type MessageType = 'text' | 'image' | 'file' | 'gif';

export type LinkPreviewProvider = 'generic' | 'youtube' | 'x' | 'tiktok' | 'facebook';
export type LinkPreviewEmbedType = 'none' | 'youtube-video' | 'x-post' | 'tiktok-video';

export interface LinkPreview {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
  provider?: LinkPreviewProvider;
  embedType?: LinkPreviewEmbedType;
  providerId?: string;
  canonicalUrl?: string;
  embedUrl?: string;
  thumbnailUrl?: string;
  authorName?: string;
  authorUrl?: string;
  htmlWidth?: number;
  htmlHeight?: number;
}

export interface MessageContent {
  text?: string;
  mediaUrl?: string;
  mediaType?: string;
  fileName?: string;
  fileSize?: number;
  thumbnailUrl?: string;
  linkPreview?: LinkPreview;
  /** Original image width in pixels (for aspect ratio calculation) */
  imageWidth?: number;
  /** Original image height in pixels (for aspect ratio calculation) */
  imageHeight?: number;
  /** Giphy GIF ID (for gif type messages) */
  giphyId?: string;
  /** Original full-resolution Giphy URL (for full-screen viewer) */
  giphyOriginalUrl?: string;
  /** Scripture verse reference (for scripture card display) */
  scripture?: ScriptureReference;
  /** User-typed text accompanying a scripture attachment (shown above the card) */
  userText?: string;
}

export interface ReplyTo {
  messageId: string;
  text: string;
  userName: string;
}

export interface Mention {
  userId: string;
  userName: string;
  startIndex: number;
  endIndex: number;
}

export interface Message {
  id: string;
  organizationId: string;
  groupId: string;
  /** If set, this message belongs to a chat group; if null/absent, it's in the "Everyone" chat */
  chatGroupId?: string | null;
  userId: string;
  userName: string;
  userProfileImageUrl?: string;
  type: MessageType;
  content: MessageContent;
  replyTo?: ReplyTo;
  mentions: Mention[];
  mentionedUserIds: string[];
  reactions: Record<string, string[]>;
  reactionCounts: Record<string, number>;
  isPinned: boolean;
  pinnedAt?: FirestoreTimestampLike | string;
  pinnedBy?: string;
  isDeleted: boolean;
  isEdited: boolean;
  editedAt?: FirestoreTimestampLike | string;
  createdAt: FirestoreTimestampLike | string;
  updatedAt: FirestoreTimestampLike | string;
}

// ============================================================================
// SEND MESSAGE TYPES
// ============================================================================

export interface SendMessageData {
  type: MessageType;
  content: MessageContent;
  replyTo?: ReplyTo;
  mentions?: Mention[];
}

// ============================================================================
// CHAT SETTINGS TYPES
// ============================================================================

export type NotificationLevel = 'all' | 'mentions' | 'none';

export interface ChatSettings {
  notificationLevel: NotificationLevel;
  muteUntil?: string;
}

export interface UpdateChatSettingsPayload {
  notificationLevel?: NotificationLevel;
  muteUntil?: string | null;
}

// ============================================================================
// TYPING INDICATOR TYPES
// ============================================================================

export interface TypingUser {
  userId: string;
  userName: string;
  startedAt: number;
}

// ============================================================================
// GROUP MEMBER TYPES
// ============================================================================

export type GroupMemberRole = 'leader' | 'member';

export interface GroupMember {
  userId: string;
  userName: string;
  profileImageUrl?: string;
  role: GroupMemberRole;
}

// ============================================================================
// CHAT ANALYTICS TYPES
// ============================================================================

export interface ChatAnalyticsDaily {
  date: string;
  messageCount: number;
  uniqueSenders: number;
  imageCount: number;
  documentCount: number;
  linkCount: number;
  reactionCount: number;
  mentionCount: number;
  peakHour?: number;
}

// ============================================================================
// MEDIA UPLOAD TYPES
// ============================================================================

export interface UploadProgress {
  progress: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  error?: string;
}

export interface UploadResult {
  url: string;
  thumbnailUrl?: string;
  mimeType: string;
  fileSize: number;
  fileName: string;
}

// ============================================================================
// REACTION TYPES — moved to src/config/emoji-config.ts
// ============================================================================

// ============================================================================
// CHAT GROUP TYPES
// ============================================================================

export type ChatGroupStatus = 'active' | 'archived' | 'deleted';
export type ChatGroupType = 'group' | 'dm';

export interface ChatGroup {
  id: string;
  organizationId: string;
  groupId: string;
  name: string;
  description?: string;
  avatarUrl?: string;
  ownerUserId: string;
  addMembersRestricted: boolean;
  type: ChatGroupType;
  status: ChatGroupStatus;
  memberCount: number;
  lastMessageText?: string;
  lastMessageUserName?: string;
  lastMessageAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ChatGroupWithUnread extends ChatGroup {
  unreadCount: number;
  isOwner: boolean;
  /** For DMs: the other participant's display name */
  dmPartnerName?: string;
  /** For DMs: the other participant's avatar URL */
  dmPartnerAvatarUrl?: string;
  /** For DMs: the other participant's userId */
  dmPartnerUserId?: string;
}

export interface CreateChatGroupData {
  name: string;
  description?: string;
  memberIds: string[];
}

// ============================================================================
// HOOK RETURN TYPES
// ============================================================================

export interface UseGroupChatReturn {
  messages: Message[];
  pinnedMessages: Message[];
  loading: boolean;
  hasMore: boolean;
  isScrollContextActive: boolean;
  clearScrollContext: () => void;
  loadMore: () => Promise<void>;
  sendMessage: (data: SendMessageData) => Promise<void>;
  editMessage: (messageId: string, newText: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  addReaction: (messageId: string, emoji: string) => Promise<void>;
  removeReaction: (messageId: string, emoji: string) => Promise<void>;
  typingUsers: TypingUser[];
  setTyping: (isTyping: boolean) => void;
  isSyncing: boolean;
  pendingCount: number;
  error: string | null;
  _handleRemoteTyping: (typingUser: TypingUser, isTyping: boolean) => void;
}

export interface UseChatSettingsReturn {
  settings: ChatSettings | null;
  loading: boolean;
  error: string | null;
  updateSettings: (updates: UpdateChatSettingsPayload) => Promise<void>;
  saving: boolean;
}

export interface UseMediaUploadReturn {
  uploadFile: (file: File) => Promise<UploadResult>;
  progress: UploadProgress | null;
  cancelUpload: () => void;
}

export interface UseChatGroupsReturn {
  chatGroups: ChatGroupWithUnread[];
  everyoneUnreadCount: number;
  everyoneLastMessageText: string | null;
  everyoneLastMessageUserName: string | null;
  everyoneLastMessageAt: string | null;
  loading: boolean;
  error: string | null;
  createChatGroup: (data: CreateChatGroupData) => Promise<ChatGroup>;
  /** Find or create a DM with a target user. Returns the chat group and whether it was newly created. */
  createDM: (targetUserId: string) => Promise<{ chatGroup: ChatGroup; isNew: boolean }>;
  refresh: () => void;
}

// ============================================================================
// AI CHAT SEARCH TYPES
// ============================================================================

export interface ChatSearchMessage {
  id: string;
  firestoreId?: string;
  archiveId?: string;
  userId: string;
  userName: string;
  messageType: string;
  contentText: string;
  chatGroupId?: string | null;
  chatGroupName?: string | null;
  createdAt: string;
  isArchived: boolean;
  searchType?: 'semantic' | 'exact' | 'both' | 'local' | string;
  relevanceScore?: number;
  degraded?: boolean;
}

export interface ChatSearchMeta {
  totalResults: number;
  semanticResults: number;
  fullTextResults: number;
  latencyMs: number;
}

export interface ChatSearchResponse {
  answer: string;
  messages: ChatSearchMessage[];
  searchMeta: ChatSearchMeta;
}

// ============================================================================
// QUICK CHAT SEARCH TYPES
// ============================================================================

export interface ChatQuickSearchMeta {
  totalResults: number;
  semanticResults: number;
  exactResults: number;
  latencyMs: number;
  mode: 'hybrid';
  degraded?: boolean;
}

export interface ChatQuickSearchResponse {
  messages: ChatSearchMessage[];
  searchMeta: ChatQuickSearchMeta;
}
